import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { whatsAppLink, WhatsAppIcon } from "@/components/WhatsAppButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallbackDialog({ triggerTestId = "request-callback-button", triggerClassName = "", children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/callbacks`, form);
      setSubmitted(form);
      toast.success("Callback request received! We'll call you soon.");
      setForm({ name: "", phone: "", preferred_time: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" data-testid={triggerTestId}
            className={`rounded-full border-slate-300 px-6 h-10 text-sm font-medium transition-transform duration-200 hover:-translate-y-0.5 hidden lg:inline-flex ${triggerClassName}`}>
            <PhoneCall className="w-4 h-4 mr-2" /> Request Callback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="callback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request a callback</DialogTitle>
          <DialogDescription>Leave your number — a Solve Desk expert will call you back, usually within a few hours.</DialogDescription>
        </DialogHeader>
        {submitted ? (
          <div className="text-center py-4" data-testid="callback-success">
            <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </span>
            <h3 className="font-display text-2xl font-semibold text-slate-900">We'll call you soon!</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
              Can't wait? Start the conversation on WhatsApp right now — your details are already typed in.
            </p>
            <a href={whatsAppLink(`Hi Solve Desk! I just requested a callback.\n\nName: ${submitted.name}\nPhone: ${submitted.phone}${submitted.preferred_time ? `\nPreferred time: ${submitted.preferred_time}` : ""}`)}
              target="_blank" rel="noopener noreferrer" className="block mt-6">
              <Button data-testid="callback-whatsapp-continue"
                className="w-full h-13 py-3.5 rounded-full text-base font-semibold bg-[#25D366] hover:bg-[#1fb857] text-white shadow-lg shadow-[#25D366]/25 transition-transform duration-200 hover:-translate-y-0.5">
                <WhatsAppIcon className="mr-2 w-5 h-5" /> Continue on WhatsApp
              </Button>
            </a>
            <button onClick={() => { setSubmitted(null); setOpen(false); }} data-testid="callback-done-close"
              className="mt-4 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 underline underline-offset-4">
              Done
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="callback-form">
          <div className="space-y-1.5">
            <Label htmlFor="cb-name">Full Name</Label>
            <Input id="cb-name" data-testid="callback-name-input" placeholder="Your name" className="h-12 rounded-xl"
              value={form.name} onChange={set("name")} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-phone">Mobile / WhatsApp No.</Label>
            <Input id="cb-phone" data-testid="callback-phone-input" placeholder="+91 98XXX XXXXX" className="h-12 rounded-xl"
              value={form.phone} onChange={set("phone")} required />
          </div>
          <div className="space-y-1.5">
            <Label>Preferred time <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
              <SelectTrigger data-testid="callback-time-select" className="h-12 rounded-xl">
                <SelectValue placeholder="Any time works" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Morning (10am – 12pm)">Morning (10am – 12pm)</SelectItem>
                <SelectItem value="Afternoon (12pm – 4pm)">Afternoon (12pm – 4pm)</SelectItem>
                <SelectItem value="Evening (4pm – 7pm)">Evening (4pm – 7pm)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} data-testid="callback-submit-button"
            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/25">
            {loading ? "Sending..." : "Call Me Back"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
